import React, { useState, useEffect } from 'react';
import { authService, setAuthToken } from '../services/api';
import { socketService } from '../services/socketService';
import logoImage from './830437_orig.png';
import './WelcomePage.css';

export default function WelcomePage({ onLogin }) {
  // Registration form state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regMembership, setRegMembership] = useState('');
  const [regMessage, setRegMessage] = useState('');
  const [regSuccess, setRegSuccess] = useState(null);
  const [regLoading, setRegLoading] = useState(false);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginMessage, setLoginMessage] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(null);
  const [loginPreviewUrl, setLoginPreviewUrl] = useState('');
  // Manual login state
  const [showManual, setShowManual] = useState(false);
  const [isPwa, setIsPwa] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const [manualMessage, setManualMessage] = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [manualSuccess, setManualSuccess] = useState(null);
  // Live connection status
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [countdownActive, setCountdownActive] = useState(false);

  // New: handle registration via API
  const handleRegister = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    setRegMessage('');
    setRegSuccess(null);
    
    try {
      const res = await authService.register(regName.trim(), regEmail.trim(), regMembership.trim());
      // Use server-provided message
      const message = res.message;
      setRegMessage(message);
      setRegSuccess(res.success);
      setRegName('');
      setRegEmail('');
      setRegMembership('');
    } catch (err) {
      // Display server-provided error message if available
      const message = err.response?.data?.message || 'Registration failed.';
      setRegMessage(message);
      setRegSuccess(false);
    }
    setRegLoading(false);
  };

  // Send magic link to approved user email
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginMessage('');
    setLoginSuccess(null);
    setLoginPreviewUrl('');
    try {
      const res = await authService.loginRequest(loginEmail.trim());
      setLoginMessage(res.message);
      setLoginSuccess(res.success);
      if (res.previewUrl) {
        setLoginPreviewUrl(res.previewUrl);
      }
      if (res.magicLink) {
        setShowManual(true);
        setManualUrl(res.magicLink);
        setManualMessage('Local test mode is active. The generated magic link is prefilled below.');
        setManualSuccess(true);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'No user registered to this email address found';
      setLoginMessage(message);
      setLoginSuccess(false);
    }
  };

  // Manage socket connection for live status
  useEffect(() => {
    const socket = socketService.connect();
    setIsSocketConnected(socketService.isConnected());
    const offConnect = socketService.onConnect(() => {
      setIsSocketConnected(true);
      setCountdownActive(false);
      setCountdown(60);
    });
    const offDisconnect = socketService.onDisconnect(() => {
      setIsSocketConnected(false);
      setCountdownActive(true);
      setCountdown(60);
    });
    return () => {
      offConnect();
      offDisconnect();
      socketService.disconnect();
    };
  }, []);

  // Detect if the app is running as a standalone PWA (launched from home screen)
  useEffect(() => {
    const checkPwa = () => {
      const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
      const isiOS = window.navigator && window.navigator.standalone === true;
      setIsPwa(Boolean(isStandalone || isiOS));
    };

    checkPwa();
    // also listen for changes (some browsers update display-mode on install)
    const mq = window.matchMedia && window.matchMedia('(display-mode: standalone)');
    if (mq && mq.addEventListener) mq.addEventListener('change', checkPwa);
    return () => {
      if (mq && mq.removeEventListener) mq.removeEventListener('change', checkPwa);
    };
  }, []);

  // Handle manual magic-link paste when in PWA / standalone mode
  const handleManualLogin = async (e, providedUrl = null) => {
    e.preventDefault();
    setManualLoading(true);
    setManualMessage('');
    setManualSuccess(null);

    try {
      const urlText = (providedUrl || manualUrl).trim();
      if (!urlText) {
        setManualMessage('Paste the magic link URL here.');
        setManualSuccess(false);
        setManualLoading(false);
        return;
      }

      // Try to parse token from the pasted URL first
      let token = null;
      try {
        const parsed = new URL(urlText, window.location.origin);
        token = parsed.searchParams.get('token') || parsed.searchParams.get('t') || parsed.searchParams.get('auth');
      } catch (err) {
        // not a valid URL; continue to try fetching it
      }

      if (token) {
        // Use token directly
        setAuthToken(token);
        localStorage.setItem('token', token);
        const user = await authService.getCurrentUser();
        onLogin && onLogin(user);
        setManualMessage('Logged in successfully.');
        setManualSuccess(true);
        setManualLoading(false);
        return;
      }

      // If no token in URL, try fetching the URL in case server returns JSON with token
      const resp = await fetch(urlText, { method: 'GET', credentials: 'include' });

      const contentType = resp.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const body = await resp.json();
        token = body.token || body.data?.token || body.accessToken;
        if (token) {
          setAuthToken(token);
          localStorage.setItem('token', token);
          const user = body.user || (await authService.getCurrentUser());
          onLogin && onLogin(user);
          setManualMessage('Logged in successfully.');
          setManualSuccess(true);
          setManualLoading(false);
          return;
        }
      }

      // If we reach here, we couldn't extract a token programmatically
      setManualMessage('Could not extract a token from that link. Copy the magic link from your login email then return here to paste it.');
      setManualSuccess(false);
    } catch (err) {
      console.error('Manual login error:', err);
      setManualMessage('Error using the link. Please copy the link from your login email, feel free to request a new link through the form above.');
      setManualSuccess(false);
    }

    setManualLoading(false);
  };

  // Countdown timer for disconnected state
  useEffect(() => {
    if (!countdownActive) return;
    
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else {
      // countdown reached 0
      setCountdownActive(false);
      setCountdown(60);
    }
  }, [countdownActive, countdown]);

  return (
    <div className="welcome-container">
      <div className="welcome-card">
        {/* Live connection indicator */}
        <span className={`connection-status ${isSocketConnected ? 'connected' : 'disconnected'}`}>
          {isSocketConnected ? '● LIVE' : `● SERVER DISCONNECTED - PLEASE WAIT ${countdown}s`}
        </span>

        {/* Header with logo and title */}
        <div className="welcome-header">
          <div className="header-text">
            <div className="welcome-title">
              <span className="title-main">
              <img src={require('./830437_orig.png')} alt="BKER Logo" style={{ height: 48, verticalAlign: 'middle' }} />
              </span>
              <span className="title-subtitle">Wingfoil Booking</span>
            </div>
          </div>
          {/* <img src={logoImage} alt="BKER Logo" className="logo-image" /> */}
        </div>

        {/* Registration Section */}
        <div className="form-section register">
          <h2>Request Access</h2>
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <input
                type="text"
                placeholder="Full Name"
                value={regName}
                onChange={e => setRegName(e.target.value)}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <input
                type="email"
                placeholder="Email Address"
                value={regEmail}
                onChange={e => setRegEmail(e.target.value)}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                placeholder="Sailing Membership Number"
                value={regMembership}
                onChange={e => setRegMembership(e.target.value)}
                className="form-input"
                required
              />
            </div>
            <button 
              type="submit" 
              disabled={regLoading}
              className={`form-button ${regLoading ? 'loading' : ''}`}
            >
              {regLoading ? '' : 'Request Access'}
            </button>
            {regMessage && (
              <div className={`form-message ${regSuccess ? 'success' : 'error'}`}>
                {regMessage}
              </div>
            )}
          </form>
        </div>

        {/* Login Section */}
        <div className="form-section login">
          <h2>Member Login</h2>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <input
                type="email"
                placeholder="Email Address"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                className="form-input"
                required
              />
            </div>
            <button type="submit" className="form-button">
              Send Magic Link to Email
            </button>
                        <a 
              href="#" 
              onClick={async (e) => { 
                e.preventDefault(); 
                setManualMessage('');
                setManualSuccess(null);
                try {
                  const clipboardText = await navigator.clipboard.readText();
                  setManualUrl(clipboardText);
                  setShowManual(true);
                  // Auto-submit immediately with the pasted text
                  handleManualLogin({ preventDefault: () => {} }, clipboardText);
                } catch (err) {
                  console.error('Failed to read clipboard:', err);
                  setShowManual(true); // Still show the manual input if clipboard fails
                }
              }} 
              onContextMenu={(e) => e.preventDefault()}
              className="manual-link"
            >
              Paste Login Link
            </a>
            {loginMessage && (
              <div className={`form-message ${loginSuccess ? 'success' : 'error'}`}>
                {loginMessage}
              </div>
            )}
            {loginPreviewUrl && (
              <div className="form-message success">
                <a href={loginPreviewUrl} target="_blank" rel="noreferrer">
                  Open test email preview
                </a>
              </div>
            )}
          </form>
          {/* Manual login row */}
          {showManual && (
            <>
              <div className="manual-row">
                <input
                  type="text"
                  placeholder="Paste magic link URL here"
                  value={manualUrl}
                  onChange={e => setManualUrl(e.target.value)}
                  className="manual-input"
                />
                <button type="button" onClick={handleManualLogin} className={`manual-button ${manualLoading ? 'loading' : ''}`}>
                  {manualLoading ? '' : 'Login'}
                </button>
              </div>
              {manualMessage && (
                <div className={`form-message ${manualSuccess ? 'success' : 'error'}`}>
                  {manualMessage}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
